import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';

import FormSelect from '../../components/form/FormSelect';

import {
  agentViewTemplatesGet,
  sendMetaTemplateMsg
} from '../../store/slices/tenantSlices/metaHelpDeskSlice';

const SendTemplateModal = ({ isOpen, onCloseClick, from, to }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [isSending, setIsSending] = useState(false);

  const fetchTemplates = async () => {
    try {
      const response = await dispatch(agentViewTemplatesGet()).unwrap();
      setTemplates(response?.data || []);
    } catch (error) {
      setTemplates([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const templateOptions = templates?.map((val) => ({
    label: val?.name,
    value: val?.id
  }));

  const selectedTemplate = templates?.find((val) => val?.id === selectedTemplateId) || null;

  const handleSend = async () => {
    if (!selectedTemplateId || isSending) return false;

    if (!from || !to) {
      toast.error('Sender or recipient number is missing');
      return false;
    }

    setIsSending(true);
    try {
      const payload = { from, to, template_id: selectedTemplateId };
      const response = await dispatch(sendMetaTemplateMsg(payload)).unwrap();
      if (response && response?.message) {
        toast.success(response?.message);
        onCloseClick(true);
      }
      setIsSending(false);
    } catch (error) {
      setIsSending(false);
      console.log('error', error);
    }
  };

  return (
    <Modal isOpen={isOpen} size="md" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">Send Template Message</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <FormSelect
          name="template_id"
          label="Template"
          placeholder="Select Template"
          value={selectedTemplateId}
          options={templateOptions}
          onChange={(e) => setSelectedTemplateId(e.target.value)}
        />
        {selectedTemplate ? (
          <div className="border rounded p-2 mt-2" style={{ maxHeight: '35vh', overflowY: 'auto' }}>
            <div className="d-flex justify-content-between align-items-center gap-2">
              <span className="text-secondary">Preview</span>
              <span className="badge bg-secondary text-uppercase">
                {selectedTemplate?.message_type}
              </span>
            </div>
            {selectedTemplate?.header_text ? (
              <div className="fw-bold mt-2">{selectedTemplate?.header_text}</div>
            ) : null}
            <div className="mt-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {selectedTemplate?.body}
            </div>
            {selectedTemplate?.footer ? (
              <div className="small text-secondary mt-1">{selectedTemplate?.footer}</div>
            ) : null}
            {selectedTemplate?.buttons?.length > 0 ? (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {selectedTemplate?.buttons?.map((button, index) => (
                  <span key={index} className="badge bg-light text-primary border">
                    {button?.title}
                  </span>
                ))}
              </div>
            ) : null}
            {selectedTemplate?.list_sections?.length > 0
              ? selectedTemplate?.list_sections?.map((section, index) => (
                  <div key={index} className="mt-2">
                    <div className="small text-secondary">{section?.title}</div>
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {section?.rows?.map((row, rowIndex) => (
                        <span key={rowIndex} className="badge bg-light text-primary border">
                          {row?.title}
                        </span>
                      ))}
                    </div>
                  </div>
                ))
              : null}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-center">
        <Button
          disabled={isSending}
          onClick={() => onCloseClick()}
          className="px-5 bg-transparent text-primary">
          {t('cancel')}
        </Button>
        <Button
          type="button"
          disabled={!selectedTemplateId || isSending}
          onClick={() => handleSend()}
          className="px-5 bg-primary border-primary">
          {isSending ? <Spinner size="sm" /> : 'Send'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SendTemplateModal.propTypes = {
  isOpen: PropTypes.bool,
  onCloseClick: PropTypes.func,
  from: PropTypes.string,
  to: PropTypes.string
};

export default SendTemplateModal;
